import moment from 'moment'
import { ISO_DATE_SHORT_FORMAT } from 'constants/dateFormats'
import { CRUISE_ROUTES } from 'constants/cruise'
import { serializeCruiseLinesForUrl } from './cruiseUtils'

export const SIMILAR_CRUISES_MIN_RESULTS = 3
export const SIMILAR_CRUISES_PRICE_RANGE_PERCENT = 0.15

export interface SimilarCruiseSearchContext {
  cabinType: string
  cruiseLineCode?: string
  departureDate?: string | null
  arrivalDate?: string | null
  departurePlace?: SimilarCruisePlace
  destinationPlace?: SimilarCruisePlace
  offer: App.CruiseOffer
}

export interface SimilarCruisePlace {
  id: string
  name: string
}

export interface SimilarCruiseSearchCandidate {
  filters: App.OfferListFilters
  to: string
}

export interface SimilarCruiseSearchCandidates {
  mostRestrictive: SimilarCruiseSearchCandidate
  withoutPriceRange: SimilarCruiseSearchCandidate
  withoutDestination: SimilarCruiseSearchCandidate
  withoutCruiseLine: SimilarCruiseSearchCandidate
  cabinOnly: SimilarCruiseSearchCandidate
  monthOnly: SimilarCruiseSearchCandidate
}

export interface SimilarCruiseSearchSelection {
  candidate: SimilarCruiseSearchCandidate
  pendingCandidate?: SimilarCruiseSearchCandidate
}

type SimilarCruiseFilterOverrides = {
  cabinTypes?: Array<string>
  cruiseLines?: Array<string>
  departurePlaceId?: string
  departureName?: string
  destinationId?: string
  destinationName?: string
  minPrice?: number
  maxPrice?: number
}

export function getDepartureMonthsForDateRange(departureDate?: string | null, arrivalDate?: string | null): Array<string> {
  if (!departureDate) return []

  const start = moment(departureDate)
  const end = moment(arrivalDate || departureDate)

  if (!start.isValid()) return []

  const current = start.clone().startOf('month')
  const finalMonth = end.isValid() ? end.clone().startOf('month') : current
  const departureMonths: Array<string> = []

  while (current.isSameOrBefore(finalMonth, 'month')) {
    departureMonths.push(current.format(ISO_DATE_SHORT_FORMAT))
    current.add(1, 'month')
  }

  return departureMonths
}

export function getLowestKnownCabinPrice(offer: App.CruiseOffer, cabinType: string): number | undefined {
  const cabinTypeKey = cabinType.toLowerCase()
  const cabinPrices = Object.values(offer.lowestDeparturePricesByCategory)
    .flatMap(pricesByCabinCategory => Object.entries(pricesByCabinCategory))
    .filter(([category]) => category.toLowerCase() === cabinTypeKey)
    .map(([, price]) => price.total)
    .filter((price): price is number => typeof price === 'number' && price > 0)

  if (!cabinPrices.length) return undefined

  return Math.min(...cabinPrices)
}

export function getPriceRange(price?: number): Pick<App.OfferListFilters, 'minPrice' | 'maxPrice'> {
  if (!price) return {}

  return {
    minPrice: Math.floor(price * (1 - SIMILAR_CRUISES_PRICE_RANGE_PERCENT)),
    maxPrice: Math.ceil(price * (1 + SIMILAR_CRUISES_PRICE_RANGE_PERCENT)),
  }
}

function normalizeCruisePortName(portName: string): string {
  return portName
    .toLowerCase()
    .replace(/\([^)]*\)/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
}

function getCruisePortLabelVariants(portName: string): Array<string> {
  const [shortName] = portName.split(',')

  return [portName, shortName].map(normalizeCruisePortName).filter(Boolean)
}

export function resolveCruisePortFacet(
  portName: string | undefined,
  portFacets: Array<App.PortFacetV2Item>,
): SimilarCruisePlace | undefined {
  if (!portName) return undefined

  const portNameVariants = getCruisePortLabelVariants(portName)

  const portFacet = portFacets.find(facet => {
    const facetVariants = getCruisePortLabelVariants(facet.label)
    return facetVariants.some(facetVariant => portNameVariants.includes(facetVariant))
  })

  if (!portFacet) return undefined

  return {
    id: portFacet.slug,
    name: portFacet.label.split(',')[0],
  }
}

function getSearchUrl(filters: App.OfferListFilters): string {
  const params = new URLSearchParams()

  if (filters.departureMonths?.length) {
    params.set('departureMonths', filters.departureMonths.filter(Boolean).join(','))
  }
  filters.cabinTypes?.forEach(cabinType => params.append('cabinTypes', cabinType))
  if (filters.departurePlaceId) params.set('departureIds', filters.departurePlaceId)
  if (filters.departureName) params.set('departureNames', filters.departureName)
  if (filters.destinationId) params.set('destinationIds', filters.destinationId)
  if (filters.destinationName) params.set('destinationNames', filters.destinationName)
  const cruiseLines = serializeCruiseLinesForUrl(filters.cruiseLines)
  if (cruiseLines) params.set('cruiseLines', cruiseLines)
  if (filters.minPrice) params.set('minPrice', String(filters.minPrice))
  if (filters.maxPrice) params.set('maxPrice', String(filters.maxPrice))

  const query = params.toString()
  return query ? `${CRUISE_ROUTES.SEARCH_PAGE}?${query}` : CRUISE_ROUTES.SEARCH_PAGE
}

function buildCandidate(departureMonths: Array<string>, overrides: SimilarCruiseFilterOverrides): SimilarCruiseSearchCandidate {
  const filters: App.OfferListFilters = {
    offerTypes: ['cruise'],
    departureMonths,
    ...overrides,
  }

  return {
    filters,
    to: getSearchUrl(filters),
  }
}

export function buildSimilarCruiseSearchCandidates(context: SimilarCruiseSearchContext): SimilarCruiseSearchCandidates {
  const departureMonths = getDepartureMonthsForDateRange(context.departureDate, context.arrivalDate)

  if (!departureMonths.length) {
    const fallbackCandidate = buildCandidate([], {})
    return {
      mostRestrictive: fallbackCandidate,
      withoutPriceRange: fallbackCandidate,
      withoutDestination: fallbackCandidate,
      withoutCruiseLine: fallbackCandidate,
      cabinOnly: fallbackCandidate,
      monthOnly: fallbackCandidate,
    }
  }

  const cabinTypes = [context.cabinType]
  const cruiseLines = context.cruiseLineCode ? [context.cruiseLineCode] : undefined
  const priceRange = getPriceRange(getLowestKnownCabinPrice(context.offer, context.cabinType))
  const departurePlaceFilter = context.departurePlace ? {
    departurePlaceId: context.departurePlace.id,
    departureName: context.departurePlace.name,
  } : {}
  const destinationFilter = context.destinationPlace ? {
    destinationId: context.destinationPlace.id,
    destinationName: context.destinationPlace.name,
  } : {}

  const withCabinBase = {
    cabinTypes,
    ...departurePlaceFilter,
    ...(cruiseLines && { cruiseLines }),
    ...destinationFilter,
    ...priceRange,
  }

  return {
    mostRestrictive: buildCandidate(departureMonths, withCabinBase),
    withoutPriceRange: buildCandidate(departureMonths, {
      ...withCabinBase,
      minPrice: undefined,
      maxPrice: undefined,
    }),
    withoutDestination: buildCandidate(departureMonths, {
      ...withCabinBase,
      minPrice: undefined,
      maxPrice: undefined,
      destinationId: undefined,
      destinationName: undefined,
    }),
    withoutCruiseLine: buildCandidate(departureMonths, {
      ...withCabinBase,
      minPrice: undefined,
      maxPrice: undefined,
      destinationId: undefined,
      destinationName: undefined,
      cruiseLines: undefined,
    }),
    cabinOnly: buildCandidate(departureMonths, {
      cabinTypes,
    }),
    monthOnly: buildCandidate(departureMonths, {}),
  }
}

export function selectSimilarCruiseSearchCandidate(
  candidates: SimilarCruiseSearchCandidates,
  getCount: (filters: App.OfferListFilters) => number | undefined,
): SimilarCruiseSearchSelection {
  if (getCount(candidates.mostRestrictive.filters) === undefined) {
    return {
      candidate: candidates.mostRestrictive,
      pendingCandidate: candidates.mostRestrictive,
    }
  }

  const cabinOnlyCount = getCount(candidates.cabinOnly.filters)
  const cabinCandidates = [
    candidates.mostRestrictive,
    candidates.withoutPriceRange,
    candidates.withoutDestination,
    candidates.withoutCruiseLine,
    candidates.cabinOnly,
  ]

  for (const candidate of cabinCandidates) {
    const count = getCount(candidate.filters)
    if (count === undefined) {
      return {
        candidate,
        pendingCandidate: candidate,
      }
    }
    if (count >= SIMILAR_CRUISES_MIN_RESULTS) {
      return {
        candidate,
      }
    }
  }

  if (cabinOnlyCount === 0) {
    return {
      candidate: candidates.monthOnly,
    }
  }

  return {
    candidate: candidates.cabinOnly,
  }
}
